import StageQr from "@/components/StageQr";
import { Fragment } from "react";
import EquipmentOptions from "@/components/EquipmentOptions";
import { useEmployees } from "@/lib/employees";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { aniloxRows, ANILOX_COMBOS, INK_MANUFACTURERS } from "@/components/Shared";

// "3. Printing Process · Line Clearance · Production Details", the paper form PF005/F02-00,
// as the Printing stage's record. The reconciliation table has one row per run; the
// stage's A-D figures (which the waste alerts and the AVT stage read) are its totals.
const MIN_ROWS = 4;
const RUN_KEYS = ["issued", "make_ready", "total_counter", "partial_counter", "operator"];
const blankRun = () => ({ issued: "", make_ready: "", total_counter: "", partial_counter: "", operator: "" });
const has = (v) => v !== undefined && v !== null && v !== "";
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => (Math.round(n * 1000) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 3 });

// Fields of this stage the sheet covers; the rest stay available under "More details".
export const SHEET_FIELDS = new Set([
  "equipment_id", "previous_job_name", "previous_product_code", "lc_done", "lc_checked", "lc_done_by", "lc_checked_by",
  "date", "start_time", "end_time", "run_rows", "issued_a_meters", "make_ready_wastage_b", "total_counter_c",
  "partial_counter_d", "in_process_wastage_e", "total_consumption_f", "qty_returned_to_stores", "no_of_printed_rolls",
  "qty_forward_to_avt_labels", "master_reels", "picks_for_reference", "remarks", "done_by_production", "checked_by_qc",
  "repeat_length_mm", "no_of_ups", "anilox_details",
]);

/** The run rows: as saved, or, for records made before the table existed, one row from the single A-D figures. */
export function runRows(data) {
  if (Array.isArray(data.run_rows) && data.run_rows.length) return data.run_rows;
  const legacy = ["issued_a_meters", "make_ready_wastage_b", "total_counter_c", "partial_counter_d"];
  if (!legacy.some((k) => has(data[k]))) return [];
  return [{ issued: data.issued_a_meters ?? "", make_ready: data.make_ready_wastage_b ?? "",
    total_counter: data.total_counter_c ?? "", partial_counter: data.partial_counter_d ?? "", operator: "" }];
}

const sum = (rows, key) => rows.reduce((s, r) => s + num(r[key]), 0);

/** A, B, C, D and what follows from them (the sheet's formulas). No. of Printed Rolls isn't one of
 * these — it's how many physical master rolls the operator wound the printed material onto (often
 * just 1 for the whole run), a judgment call with no formula behind it, so it stays a plain manual field. */
export function figures(data) {
  const rows = runRows(data);
  const A = sum(rows, "issued"), B = sum(rows, "make_ready"), C = sum(rows, "total_counter"), D = sum(rows, "partial_counter");
  const ups = num(data.no_of_ups), length = num(data.repeat_length_mm);
  // Quantity forwarded: D running metres x 1000 x ups / repeat length (mm) = labels
  const labels = D > 0 && ups > 0 && length > 0 ? Math.round((D * 1000 * ups) / length) : null;
  return { A, B, C, D, E: C - D, F: B + C, returned: A - (B + C), labels };
}

/** The stage fields to save for the current rows: the A-D totals other stages read, plus the labels
 * forwarded to AVT. No. of Printed Rolls is never touched here — it's the operator's own entry. */
export function totalsPatch(data) {
  const rows = runRows(data);
  if (!rows.length) return {};
  const f = figures(data);
  const patch = {
    issued_a_meters: f.A, make_ready_wastage_b: f.B, total_counter_c: f.C, partial_counter_d: f.D,
    in_process_wastage_e: f.E, total_consumption_f: f.F, qty_returned_to_stores: f.returned,
  };
  if (f.labels !== null) patch.qty_forward_to_avt_labels = f.labels;
  return patch;
}

const line = "border border-neutral-700";
const cell = `${line} px-1.5 py-1 text-[11px] align-middle`;
const input = "w-full bg-transparent outline-none text-[11px] font-medium placeholder:text-neutral-300 focus:bg-orange-50 disabled:text-neutral-800";
const dotted = "border-b border-dotted border-neutral-600 pb-0.5";

/** `data` is the stage record, `ctx` its context (the job), `setMany(patch)` changes several keys at once. */
export default function PrintingSheet({ ctx, data, setMany, readOnly }) {
  const common = ctx?.common || {};
  const staff = useEmployees(["Printing", "Printing IPQC"]);
  const rows = runRows(data);
  const shown = [...rows, ...Array.from({ length: Math.max(MIN_ROWS - rows.length, 0) }, blankRun)];
  const f = figures(data);

  // Every edit keeps the totals (and the labels forwarded) in step with the rows.
  const change = (patch) => setMany({ ...patch, ...totalsPatch({ ...data, ...patch }) });
  const editRun = (i, key, value) => {
    const next = shown.map((r) => ({ ...r }));
    next[i][key] = value;
    change({ run_rows: next.filter((r, at) => at < rows.length || RUN_KEYS.some((k) => has(r[k]))) });
  };

  // Render helpers, not components: a component defined here would remount on every keystroke.
  const text = (label, k, { type = "text", width = "", suffix = "" } = {}) => (
    <div key={k} className={`flex items-end gap-1.5 ${width}`}>
      <span className="text-[11px] whitespace-nowrap">{label}</span>
      <input type={type} list={k === "equipment_id" ? "equipment-ids" : undefined} className={`${input} ${dotted}`} value={data[k] ?? ""} disabled={readOnly}
        onChange={(e) => (k === "no_of_ups" || k === "repeat_length_mm" ? change({ [k]: e.target.value }) : setMany({ [k]: e.target.value }))}
        data-testid={`printing-${k}`} />
      {suffix && <span className="text-[11px]">{suffix}</span>}
    </div>
  );
  const tick = (label, k) => (
    <label key={k} className="inline-flex items-center gap-1.5 text-[11px] whitespace-nowrap">
      <input type="checkbox" className="accent-[#FF5A00] h-3.5 w-3.5" checked={!!data[k]} disabled={readOnly}
        onChange={(e) => setMany({ [k]: e.target.checked })} data-testid={`printing-${k}`} />
      {label}
    </label>
  );
  const calc = (n) => (rows.length ? fmt(n) : "");
  const anilox = aniloxRows(data.anilox_details, common);
  const editAnilox = (i, key, value) => {
    const next = anilox.map((r) => ({ ...r }));
    next[i][key] = value;
    setMany({ anilox_details: next });
  };

  return (
    <div className="bg-white border border-neutral-700 text-neutral-900" data-testid="printing-sheet">
      <EquipmentOptions types={["Press"]} />
      <div className="flex items-start justify-between px-3 pt-2 pb-1">
        <div>
          <div className="font-bold text-sm">MedPack India Pvt. Ltd.</div>
          <div className="print-only text-[10px] text-neutral-700">
            BPR {common.mpwo_number} · {[common.customer_name, common.product_name].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="flex items-start gap-2"><StageQr jobId={ctx.job_id} stage="printing" /><div className="text-[11px] text-neutral-600">Page 03 of 11</div></div>
      </div>

      <div className="px-3 pb-2 space-y-1.5">
        <div className="font-bold text-[12px]">Printing Process:</div>
        <div className={`relative ${line} rounded-sm pt-3 p-2 mt-2.5`}>
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-2 text-[12px] font-bold underline whitespace-nowrap">Line Clearance</div>
          <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
            {text("Equipment I.D.:", "equipment_id")}
            {text("Repeat Length:", "repeat_length_mm", { suffix: "mm" })}
            {text("Ups:", "no_of_ups")}
            {text("Previous Job Name:", "previous_job_name")}
            <div className="flex items-end gap-1.5">
              <span className="text-[11px] whitespace-nowrap">W/o No.:</span>
              <span className={`${dotted} flex-1 text-[11px] font-semibold font-mono-num`}>{common.mpwo_number}</span>
            </div>
            {text("Previous Product Code:", "previous_product_code")}
          </div>
        </div>

        <div className={`${line} rounded-sm`}>
          <table className="w-full border-collapse">
            <tbody>
              <tr>
                <td className={`${cell} w-10 text-center font-semibold`}>3.1</td>
                <td className={`${cell} font-semibold`} colSpan={3}>Clean the printing machine and area before starting the job.</td>
              </tr>
              <tr>
                <td className={`${cell} text-center font-semibold`}>3.2</td>
                <td className={cell}>
                  Check the machine and working area before going to printing. Clear the previous run substrate before start up.
                  Printing area should be free from previous job related inks, plates and Damaged/ Rejected materials. Check the
                  same size &amp; same colour jobs are not taken for consecutive Production.
                </td>
                <td className={`${cell} w-24`}>{tick("Done", "lc_done")}</td>
                <td className={`${cell} w-24`}>{tick("Checked", "lc_checked")}</td>
              </tr>
            </tbody>
          </table>
          <div className="text-[10px] px-1.5 pt-1">Note : Return excess materials to stores, if any.</div>
          <div className="grid grid-cols-2 gap-x-6 px-1.5 pb-1.5">
            {text("Done by & Date : (Production)", "lc_done_by")}
            {text("Checked by & Date : (Q.C.)", "lc_checked_by")}
          </div>
        </div>

        <div className="font-bold text-[12px] pt-1">Production Details:</div>
        <div className="grid grid-cols-3 gap-x-4">
          {text("Date:", "date", { type: "date" })}
          {text("Start Time:", "start_time", { type: "time" })}
          {text("Finish Time:", "end_time", { type: "time" })}
        </div>

        <table className="w-full border-collapse" data-testid="printing-runs">
          <thead>
            <tr className="align-top">
              {[["Issued Qty from Stores (Meters)", "A"], ["Make Ready Wastage (Meters)", "B"], ["Total Counter (Meters)", "C"],
                ["Partial Counter (Meters)", "D"], ["In-Process Wastage (Meters)", "E(C-D)"], ["Total Consumption (Meters)", "F(B+C)"],
                ["Operator Sign", ""]].map(([h, mark]) => (
                <th key={h} className={`${cell} text-left font-semibold leading-tight`}>{h}{mark && <div>{mark}</div>}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const filled = RUN_KEYS.slice(0, 4).some((k) => has(r[k]));
              return (
                <tr key={i} className="h-7">
                  {["issued", "make_ready", "total_counter", "partial_counter"].map((k) => (
                    <td key={k} className={cell}>
                      <input type="number" className={`${input} text-right font-mono-num`} value={r[k] ?? ""} disabled={readOnly}
                        onChange={(e) => editRun(i, k, e.target.value)} data-testid={`printing-run-${i}-${k}`} />
                    </td>
                  ))}
                  <td className={`${cell} text-right font-mono-num`} data-testid={`printing-run-${i}-e`}>{filled ? fmt(num(r.total_counter) - num(r.partial_counter)) : ""}</td>
                  <td className={`${cell} text-right font-mono-num`} data-testid={`printing-run-${i}-f`}>{filled ? fmt(num(r.make_ready) + num(r.total_counter)) : ""}</td>
                  <td className={cell}>
                    {staff.length > 0 ? (
                      // the Printing staff from the employee list; a name typed before the list existed stays selectable
                      <select className={`${input} cursor-pointer`} value={r.operator ?? ""} disabled={readOnly} onChange={(e) => editRun(i, "operator", e.target.value)}
                        data-testid={`printing-run-${i}-operator`}>
                        <option value="">Select operator</option>
                        {r.operator && !staff.some((e) => e.name === r.operator) && <option value={r.operator}>{r.operator}</option>}
                        {staff.map((e) => <option key={e.code} value={e.name}>{e.name} · {e.code}</option>)}
                      </select>
                    ) : (
                      <input className={input} value={r.operator ?? ""} disabled={readOnly} onChange={(e) => editRun(i, "operator", e.target.value)}
                        data-testid={`printing-run-${i}-operator`} />
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length > 1 && (
              <tr className="bg-neutral-50 font-semibold">
                <td className={`${cell} text-right font-mono-num`}>{fmt(f.A)}</td>
                <td className={`${cell} text-right font-mono-num`}>{fmt(f.B)}</td>
                <td className={`${cell} text-right font-mono-num`}>{fmt(f.C)}</td>
                <td className={`${cell} text-right font-mono-num`}>{fmt(f.D)}</td>
                <td className={`${cell} text-right font-mono-num`}>{fmt(f.E)}</td>
                <td className={`${cell} text-right font-mono-num`}>{fmt(f.F)}</td>
                <td className={cell}>Total</td>
              </tr>
            )}
          </tbody>
        </table>
        {!readOnly && (
          <Button type="button" variant="outline" size="sm" className="no-print rounded-sm h-6 gap-1 text-[11px]"
            onClick={() => change({ run_rows: [...shown, blankRun()] })} data-testid="printing-add-run">
            <Plus size={11} /> Add run row
          </Button>
        )}

        <div className="grid grid-cols-2 gap-x-6 pt-1">
          {text("No. of Master Reels Produced:", "master_reels")}
          {text("No. of Picks Taken for References:", "picks_for_reference")}
        </div>

        <div className="font-bold text-[12px] pt-1">Anilox Used:</div>
        <table className="w-full border-collapse" data-testid="printing-anilox">
          <thead>
            <tr>
              {["Unit No.", "Pantone Colour Code", "Anilox Details", "Ink Manufacturer"].map((h) => (
                <th key={h} className={`${cell} text-left font-semibold`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {anilox.map((row, i) => (
              <tr key={row.slot}>
                <td className={`${cell} text-center font-mono-num w-16`}>{row.slot}</td>
                <td className={`${cell} font-mono-num`} data-testid={`printing-anilox-${i}-pantone`}>{row.pantone_code || "—"}</td>
                <td className={cell}>
                  <select className={`${input} cursor-pointer`} value={row.anilox_combo} disabled={readOnly}
                    onChange={(e) => editAnilox(i, "anilox_combo", e.target.value)} data-testid={`printing-anilox-${i}-combo`}>
                    <option value="">—</option>
                    {ANILOX_COMBOS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className={cell}>
                  <select className={`${input} cursor-pointer`} value={row.ink_manufacturer} disabled={readOnly}
                    onChange={(e) => editAnilox(i, "ink_manufacturer", e.target.value)} data-testid={`printing-anilox-${i}-ink`}>
                    <option value="">—</option>
                    {INK_MANUFACTURERS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div>
          <div className="text-[11px] font-bold">Remarks:</div>
          <textarea className={`${input} ${dotted} min-h-9 resize-none`} value={data.remarks ?? ""} disabled={readOnly}
            onChange={(e) => setMany({ remarks: e.target.value })} data-testid="printing-remarks" />
        </div>

        <div className="text-[11px] space-y-1.5">
          <div className="flex flex-wrap items-end justify-between gap-x-6">
            <span>
              1. Quantity returned to stores : A - F = <b className="font-mono-num" data-testid="printing-returned">{calc(f.returned)}</b> R meters.
            </span>
            {text("3. No. of Printed Rolls :", "no_of_printed_rolls", { width: "w-56" })}
          </div>
          <div>
            2. Quantity forwarded : D &nbsp;
            <Fragment>
              <span className="inline-block align-middle text-center leading-tight">
                <span className="block border-b border-neutral-700 px-1">R.meters x 1000 x ups</span>
                <span className="block">Repeat Length in mm</span>
              </span>
              {" "}= <b className="font-mono-num" data-testid="printing-labels">{f.labels !== null ? fmt(f.labels) : data.qty_forward_to_avt_labels ?? ""}</b> Labels
            </Fragment>
          </div>
          <div>Scrap the wastage quantity and enter the wastage quantity in reconciliation column on page.</div>
        </div>
        <div className="grid grid-cols-2 gap-x-6 pt-1">
          {text("Done by & Date : (Production)", "done_by_production")}
          {text("Checked by & Date : (Q.C.)", "checked_by_qc")}
        </div>
      </div>
      <div className="px-3 pb-2 text-[10px] text-neutral-600">FORMAT No. PF005/F02-00</div>
    </div>
  );
}
